import { captureException } from '@sentry/react'
import { Link as RouterLink, useHistory } from 'react-router-dom'

import { ClientError, hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Form } from '@tribeplatform/react-components/Form'
import {
  SystemSpaceSlug,
  useAuthToken,
  useLogin,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { useRefreshToken } from '../../hooks/useRefreshToken'
import { isMemberNotConfirmed } from '../../lib/app.lib'
import { useQuery } from '../../lib/useQuery'
import { Ssos } from './Ssos'
import { onAuthenticate } from './utils/onAuthenticate'

export const Login = ({ direct = false }) => {
  const history = useHistory()
  const { data: authToken } = useAuthToken() || {}
  const { network } = authToken

  const { generatePath } = useSpaceUtil()
  const { login, isLoading } = useLogin()
  const { update } = useRefreshToken()
  const [canJoinNetwork] = hasScopesPermission(network, ['joinNetwork'])

  const query = useQuery()
  const queryString: { redirect?: string; method?: string } = {}
  if (query.redirect) queryString.redirect = query.redirect
  if (query.method) queryString.method = query.method

  const onSubmit = async data => {
    try {
      const _authToken = await login({
        willReload: true,
        variables: {
          input: {
            usernameOrEmail: data?.usernameOrEmail,
            password: data?.password,
          },
        },
      })

      // TODO: remove this line after portal module is completed
      const newAuthToken = await update(_authToken)

      const { refreshToken, accessToken, member, role } = newAuthToken || {}
      if (accessToken || refreshToken) {
        if (!isMemberNotConfirmed(member, role)) {
          onAuthenticate()
          if (accessToken) {
            let redirect = query.redirect || '/'
            // prevent external redirects
            if (
              redirect.substring(0, 1) !== '/' ||
              redirect.substring(0, 2) === '//'
            )
              redirect = '/'

            if (query?.method === 'mobile') {
              window.location.href = `community://navigate${redirect}?mobileToken=${accessToken}`
            } else {
              setTimeout(() => {
                window.location.href = redirect
              }, 25)
            }
          }
        } else {
          history.replace(
            generatePath(SystemSpaceSlug.VERIFY_EMAIL, null, {
              memberId: member?.id,
            }),
          )
        }
      }
    } catch (err) {
      // better error message for `usernameOrEmail` field
      const errors = (err as ClientError)?.response?.errors
      errors?.forEach(e => {
        if (e?.code && String(e.code) !== '110') {
          captureException(e, {
            tags: {
              page: 'login',
              action: 'mutation',
            },
          })
        }
        if (e?.field === 'usernameOrEmail' && e?.message) {
          e.message = e.message.replace('usernameOrEmail', 'email')
        }
      })
      throw err
    }
  }

  const showLoginForm = direct ? true : !network.hideDefaultAuthenticationForm
  const showSsos = !direct

  return (
    <>
      <div className="lg:w-1/2 md:w-full flex mx-auto flex-col py-6 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center text-basicMain-900">
          <h2 className="mt-6 text-3xl font-extrabold">
            Sign in to your account
          </h2>
          {canJoinNetwork && (
            <div className="mt-3 text-lg">
              Don&apos;t have an account?{' '}
              <Link
                as={RouterLink}
                to={generatePath(SystemSpaceSlug.SIGNUP, null, queryString)}
              >
                Sign up now
              </Link>
            </div>
          )}
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md flex flex-col space-y-5">
          <Card className="py-8 px-4">
            {showLoginForm && (
              <>
                <Form className="space-y-5" onSubmit={onSubmit}>
                  <Form.Input
                    autoComplete="username"
                    label="Email"
                    name="usernameOrEmail"
                  />

                  <Form.Input
                    name="password"
                    type="password"
                    label="Password"
                    autoComplete="current-password"
                  />

                  <div className="text-sm text-right">
                    <Link as={RouterLink} to="/auth/forgot-password">
                      Forgot your password?
                    </Link>
                  </div>

                  <div>
                    <Button
                      fullWidth
                      variant="primary"
                      type="submit"
                      size="lg"
                      loading={isLoading}
                    >
                      Log in
                    </Button>
                  </div>
                </Form>
              </>
            )}
            {showSsos && <Ssos hasDivider={showLoginForm} />}
          </Card>
        </div>
      </div>
    </>
  )
}
